/**
 * The top bar — sticky, transparent at rest, solidified past 64px of scroll.
 *
 * Background and border are the ONLY things that change on solidify. The bar
 * never moves and never changes height, because a bar that grows or shrinks on
 * scroll makes the content under it jump.
 *
 * Two behaviours here are contract rather than decoration:
 *
 *   • **`topbar_search` is `base tier only`.** The navigation already carries a
 *     search entry from the tablet tier up, so the FIELD is withheld there and
 *     the entry is the route — a field is the worse of the two doors, because
 *     it needs a press to expand before it does anything, while the entry goes
 *     straight to the results page where queries and facets compose. It is
 *     `display: none` rather than visually hidden: a control that is not
 *     rendered must not be announced.
 *   • **The magnifier changes ROLE, not state** — an open handle while
 *     collapsed, a real submit once open, calling `requestSubmit()` so
 *     validation runs and the `submit` event fires. Collapsing is a second
 *     action with its own two handles: `Esc` (which returns focus) and a click
 *     outside. It used to toggle, so a reader who typed a query and pressed
 *     the magnifier to run it watched the field shut in their face.
 *
 * At the base tier the open field COVERS the bar rather than sharing it: at
 * 320px the mark, the theme toggle, the notification handle and the account
 * face take roughly 14rem of 20, and a six-rem box is not a field. The bar is
 * `position: sticky`, and sticky is positioned, so it is already the
 * containing block — nothing is added to carry the overlay.
 *
 * The sign-in entry of the public shell has no place here and the two never
 * co-exist: a sign-in entry beside an account avatar says the shell does not
 * know who is looking at it.
 */
import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { useTheme } from '@shared/theme/ThemeProvider';

import { AccountMenu } from './AccountMenu';
import { NavBar } from './NavBar';
import { NotificationHandle } from './NotificationHandle';
import { chromeCopy } from './copy';
import { BrandLockup, IconSearch, IconThemeDark, IconThemeLight } from './icons';
import type { GenreNode } from './GenreTree';

const SOLID_AFTER_PX = 64;

const ICON_BUTTON_CLASS =
  'flex min-h-11 w-11 shrink-0 items-center justify-center rounded-sm p-0 text-ink transition-colors duration-fast hover:bg-hover-bg hover:text-accent-text';

export interface TopbarProps {
  categories?: readonly GenreNode[];
  activeCategorySlug?: string;
}

function useSolidified(): boolean {
  const [solid, setSolid] = useState(false);

  useEffect(() => {
    let ticking = false;
    const apply = (): void => {
      ticking = false;
      setSolid(window.scrollY > SOLID_AFTER_PX);
    };
    const onScroll = (): void => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(apply);
    };

    apply();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return solid;
}

function SearchExpand() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent): void => {
      if (!wrapRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open]);

  return (
    <div
      ref={wrapRef}
      // Withheld from the tablet tier up: `topbar_search` is `base tier only`
      // and the navbar's search entry is the route there.
      className={
        open
          ? 'absolute inset-x-gutter top-1/2 z-[3] flex -translate-y-1/2 items-center gap-2 rounded-sm bg-surface px-2 py-1 shadow-elev-2 sm:static sm:flex-1 sm:translate-y-0 sm:bg-transparent sm:p-0 sm:shadow-none md:hidden'
          : 'flex items-center gap-2 md:hidden'
      }
    >
      {/*
        The results page is a route on THIS host, so this is a local navigation
        rather than a cross-host form. It used to post to `shahedni.com/search`
        in a new tab, which answered the query without the session — nothing on
        those results could be opened in place, and coming back meant a cold
        boot of the bundle.

        `onSubmit` with `useNavigate` rather than a `<Form>`: the destination is
        a different route from the one the bar is rendered on, and the bar has
        no route of its own to submit against.
      */}
      <form
        ref={formRef}
        method="get"
        onSubmit={(event) => {
          event.preventDefault();
          const query = new FormData(event.currentTarget).get('q');
          const trimmed = typeof query === 'string' ? query.trim() : '';
          // A blank submit goes to the search page anyway: the reader asked to
          // go there, and the page's own prompt is a better answer than an
          // inert control that appears to do nothing.
          navigate(trimmed ? `/search?q=${encodeURIComponent(trimmed)}` : '/search');
          setOpen(false);
        }}
        role="search"
        className={open ? 'w-full sm:w-[min(100%,22rem)]' : 'w-0 overflow-hidden opacity-0'}
      >
        <label className="visually-hidden" htmlFor="topbar-search">
          {chromeCopy.topbar.search}
        </label>
        <input
          ref={inputRef}
          id="topbar-search"
          name="q"
          type="search"
          placeholder={chromeCopy.topbar.searchPlaceholder}
          // `Esc` collapses the field and hands focus back to the button that
          // opened it. The handler sits on the INPUT rather than on the form:
          // a form is not an interactive element, and the key event that
          // matters is the one the focused control receives.
          onKeyDown={(event) => {
            if (event.key !== 'Escape') return;
            setOpen(false);
            buttonRef.current?.focus();
          }}
          className="min-h-11 w-full rounded-sm border border-border-strong bg-surface-raised px-4 py-3 text-body text-ink placeholder:text-ink-faint hover:border-ink-muted focus-visible:border-accent"
        />
      </form>

      <button
        ref={buttonRef}
        type="button"
        aria-expanded={open}
        aria-controls="topbar-search"
        aria-label={open ? chromeCopy.topbar.submitSearch : chromeCopy.topbar.openSearch}
        className={ICON_BUTTON_CLASS}
        onClick={() => {
          if (!open) {
            setOpen(true);
            return;
          }
          // Pressing it with an empty field focuses rather than submitting
          // nothing. The button is the form's SIBLING, so a press does not
          // submit on its own — and `requestSubmit()` runs validation and
          // fires `submit`, which `submit()` skips.
          if (!inputRef.current?.value.trim()) {
            inputRef.current?.focus();
            return;
          }
          formRef.current?.requestSubmit();
        }}
      >
        <IconSearch size={20} />
      </button>
    </div>
  );
}

export function Topbar({ categories = [], activeCategorySlug }: TopbarProps) {
  const solid = useSolidified();
  const { theme, toggleTheme } = useTheme();
  const isLight = theme === 'light';

  return (
    <header
      role="banner"
      data-solid={solid ? '' : undefined}
      className={[
        'sticky top-0 z-50 flex items-center gap-3 border-b border-transparent px-gutter py-3',
        'transition-[background-color,border-color] duration-fast',
        solid
          ? 'border-b-border bg-[color-mix(in_srgb,var(--surface)_92%,transparent)] shadow-elev-1 backdrop-blur-[14px] backdrop-saturate-150'
          : '',
        'md:gap-2 lg:gap-3',
      ].join(' ')}
    >
      <Link
        to="/"
        aria-label={chromeCopy.brandHomeLabel}
        className="flex shrink-0 items-center gap-2 text-ink no-underline"
      >
        {/*
          The lockup, composed by `BrandLockup` rather than assembled here, so
          the seal, the word, the gap and the accessible name stay identical to
          the footer's and the auth shell's.

          The wordmark is HIDDEN below 480px, and only in this bar: it carries
          two controls the public site's does not — the notification handle and
          the account face — so at 320px the row has about 66px of slack against
          design-system §8's `112px` lockup floor. Showing the word there would
          either overflow the window by about 38px or draw the lockup under its
          own minimum. The seal alone is the documented reduction, and the name
          stays in the accessibility tree at every width.
        */}
        <BrandLockup
          className="flex shrink-0 items-center gap-2 text-ink"
          markClassName="h-[2.5rem] w-[2.5rem] shrink-0 sm:hidden"
          lockupClassName="hidden h-[2.5rem] w-[10.36rem] shrink-0 sm:block"
          label={chromeCopy.brand}
        />
      </Link>

      <NavBar categories={categories} activeCategorySlug={activeCategorySlug} />

      <span className="ms-auto" />

      <SearchExpand />

      <button
        type="button"
        onClick={toggleTheme}
        aria-pressed={isLight}
        aria-label={isLight ? chromeCopy.topbar.toThemeDark : chromeCopy.topbar.toThemeLight}
        className={ICON_BUTTON_CLASS}
      >
        {/* The glyph names the mode the press switches TO, matching the
            label: sun while dark is on, moon while light is on. */}
        {isLight ? <IconThemeDark size={20} /> : <IconThemeLight size={20} />}
      </button>

      <NotificationHandle />
      <AccountMenu />
    </header>
  );
}
